import type { ReactNode } from 'react';
import digisakaLogo from '../../../../assets/logo-transparent.png';
import {
  buildFieldHealthViewModel,
  formatFieldHealthValue,
  trendLabel,
  type FieldHealthReportInput,
  type IndexSeriesView,
  type TrendView,
} from '../../utils/fieldHealthReportModel';
import { Sparkline, TimeSeriesChart, WeatherBarChart, WeatherLineChart } from './FieldHealthReportCharts';

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#6b7280]">{children}</p>
  );
}

function TrendPill({ trend }: { trend: TrendView }) {
  const cls =
    trend.cls === 'up'
      ? 'bg-[#e7f1ea] text-[#0e5a2f]'
      : trend.cls === 'down'
        ? 'bg-[#fff3dc] text-[#9a5b00]'
        : 'bg-[#e8eef7] text-[#1d5fa8]';
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold ${cls}`}>
      {trend.arrow} {trendLabel(trend)}
    </span>
  );
}

function StatGrid({
  items,
  cols = 4,
}: {
  items: Array<{ label: string; value: string; color?: string; sub?: string }>;
  cols?: 3 | 4;
}) {
  return (
    <div className={`grid gap-1.5 ${cols === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
      {items.map((item) => (
        <div key={item.label} className="rounded-[10px] bg-[#f5f7f4] px-2.5 py-1.5">
          <small className="block text-[10px] uppercase tracking-wide text-[#6b7280]">{item.label}</small>
          <b
            className="text-lg font-bold text-[#111827]"
            style={item.color ? { color: item.color } : undefined}
          >
            {item.value}
          </b>
          {item.sub ? <span className="block text-[10.5px] text-[#6b7280]">{item.sub}</span> : null}
        </div>
      ))}
    </div>
  );
}

function OverviewCard({ index }: { index: IndexSeriesView }) {
  return (
    <div className="rounded-[14px] border border-[#e4e8e2] bg-white px-4 py-3.5">
      <div className="flex items-center justify-between gap-2">
        <b className="text-sm font-semibold text-[#111827]">{index.label}</b>
        <span className="rounded-md border border-[#e4e8e2] px-1.5 py-px text-[10.5px] font-bold tracking-wide text-[#6b7280]">
          {index.key.toUpperCase()}
        </span>
      </div>
      <div className="font-sans text-[30px] font-bold leading-none" style={{ color: index.color }}>
        {formatFieldHealthValue(index.stats.latest)}
      </div>
      <div className="text-[11px] text-[#6b7280]">{index.latestDateLabel}</div>
      <Sparkline points={index.points} color={index.color} outliers={index.outliers} />
      <TrendPill trend={index.trend} />
    </div>
  );
}

function IndexDetailCard({ index }: { index: IndexSeriesView }) {
  return (
    <div className="mb-4 rounded-[14px] border border-[#e4e8e2] bg-white px-5 py-4">
      <div className="mb-0.5 flex items-center justify-between gap-2">
        <h3 className="font-sans text-lg font-bold text-[#111827]">
          {index.label}{' '}
          <span className="rounded-md border border-[#e4e8e2] px-1.5 py-px text-[10.5px] font-bold tracking-wide text-[#6b7280]">
            {index.key.toUpperCase()}
          </span>
        </h3>
        <TrendPill trend={index.trend} />
      </div>
      <p className="mb-1 text-[#6b7280]">{index.subtitle}</p>
      <TimeSeriesChart points={index.points} color={index.color} outliers={index.outliers} />
      <StatGrid
        items={[
          { label: 'Latest', value: formatFieldHealthValue(index.stats.latest), color: index.color },
          { label: 'Average', value: formatFieldHealthValue(index.stats.mean) },
          { label: 'Highest', value: formatFieldHealthValue(index.stats.max) },
          { label: 'Lowest', value: formatFieldHealthValue(index.stats.min) },
        ]}
      />
      {index.interpretation || index.recommendation ? (
        <div className="mt-2 rounded-lg border-l-4 border-[#008540] bg-[#e7f1ea] px-3 py-2 text-xs leading-relaxed text-[#25382c]">
          {index.interpretation ? (
            <p>
              <b>What this means ({index.latestDateLabel}):</b> {index.interpretation}
            </p>
          ) : null}
          {index.recommendation ? (
            <p className={index.interpretation ? 'mt-1' : ''}>
              <b>What to do:</b> {index.recommendation}
            </p>
          ) : null}
        </div>
      ) : null}
      {index.outlierDateLabels.map((d) => (
        <div key={d} className="mt-2 rounded-lg bg-[#fff3dc] px-3 py-2 text-[11.5px] text-[#9a5b00]">
          Reading on {d} looks cloud-affected and is not counted.
        </div>
      ))}
    </div>
  );
}

export default function FieldHealthReportView({ report }: { report: FieldHealthReportInput }) {
  const vm = buildFieldHealthViewModel(report);
  const ovCols = vm.indices.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3';

  return (
    <article className="field-health-report mx-auto max-w-[794px] bg-white text-[#111827] shadow-[0_8px_30px_rgba(0,0,0,0.18)]">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a4624] via-[#008540] to-[#1f8a4b] px-[57px] py-5 text-white">
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-[265px] w-[265px] rounded-full border border-white/15 shadow-[0_0_0_53px_rgba(255,255,255,0.04)]"
          aria-hidden
        />
        <div className="relative z-[1] flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src={digisakaLogo} alt="Digisaka" className="h-[38px] w-auto object-contain" />
            <div>
              <b className="block font-sans text-[19px] leading-tight">Digisaka</b>
              <span className="text-[10.5px] uppercase tracking-[0.14em] opacity-80">Field health report</span>
            </div>
          </div>
          <div className="text-right text-[11px] leading-snug">
            Prepared <b>{vm.preparedDate}</b>
            <br />
            {vm.preparedTime} · Philippines time
          </div>
        </div>
      </div>

      <div className="px-[57px] pb-10 pt-8">
        {/* Hero */}
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-sans text-[32px] font-bold leading-tight text-[#111827]">
              {vm.farmName}
            </h1>
            <div className="mt-0.5 text-xs text-[#6b7280]">
              Field ID {vm.farmId}
              {vm.areaLabel !== '—' ? ` · ${vm.areaLabel.replace(' ha', ' hectares')}` : ''}
            </div>
          </div>
          <span className="whitespace-nowrap rounded-full bg-[#e7f1ea] px-3 py-1 text-[11px] font-bold text-[#0e5a2f]">
            {vm.periodRange} · {vm.dayCount} days
          </span>
        </div>

        {/* Verdict */}
        <div className="mb-4 grid gap-6 rounded-[14px] border border-[#cfe3d6] bg-gradient-to-br from-[#f3faf5] to-[#e3f1e8] px-5 py-4 sm:grid-cols-[1.5fr_1fr] sm:items-center">
          <div>
            <SectionLabel>Your field in 10 seconds</SectionLabel>
            <h2 className="font-sans text-[27px] font-bold leading-tight text-[#008540]">
              {vm.verdictHeadline}
            </h2>
            <p className="mt-1 text-[13px] leading-snug text-[#374151]">{vm.verdictSummary}</p>
          </div>
          <div className="grid gap-2">
            {[
              ['Satellite readings', vm.obsCount],
              ['Days covered', vm.dayCount],
              ['Measures checked', vm.indices.length],
            ].map(([label, value]) => (
              <div
                key={String(label)}
                className="flex items-baseline justify-between rounded-[10px] border border-[#d9e8de] bg-white px-3 py-1.5"
              >
                <span className="text-[11.5px] text-[#6b7280]">{label}</span>
                <b className="text-lg font-bold text-[#008540]">{value}</b>
              </div>
            ))}
          </div>
        </div>

        {/* Overview cards */}
        <SectionLabel>Latest reading by measure</SectionLabel>
        <div className={`mb-4 grid gap-4 ${ovCols}`}>
          {vm.indices.map((index) => (
            <OverviewCard key={index.key} index={index} />
          ))}
        </div>

        {/* Field info */}
        <div className="mb-4 rounded-[14px] border border-[#e4e8e2] bg-white px-5 py-4">
          <SectionLabel>Field information</SectionLabel>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ['Farm', vm.farmName],
              ['Field ID', vm.farmId],
              ['Area', vm.areaLabel],
              ['Crop', vm.cropLabel],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <small className="block text-[10px] uppercase tracking-wide text-[#6b7280]">{label}</small>
                <b className="text-[13px] font-semibold text-[#111827]">{value}</b>
              </div>
            ))}
            <div className="col-span-2">
              <small className="block text-[10px] uppercase tracking-wide text-[#6b7280]">Period</small>
              <b className="text-[13px] font-semibold text-[#111827]">{vm.periodRange}</b>
            </div>
            <div className="col-span-2">
              <small className="block text-[10px] uppercase tracking-wide text-[#6b7280]">Measures</small>
              <b className="text-[13px] font-semibold text-[#111827]">{vm.measuresLabel}</b>
            </div>
          </div>
        </div>

        {vm.cloudOutlierCount > 0 ? (
          <div className="mb-4 rounded-lg bg-[#fff3dc] px-3 py-2 text-[11.5px] leading-snug text-[#9a5b00]">
            ⚠ {vm.cloudOutlierCount} reading{vm.cloudOutlierCount === 1 ? '' : 's'} dropped sharply and may be hidden
            by cloud or haze. They are shown as dashed circles and left out of averages and trends.
          </div>
        ) : null}

        {/* Detail charts */}
        <h2 className="font-sans text-[21px] font-bold text-[#111827]">Crop health over time</h2>
        <p className="mb-3 text-xs text-[#6b7280]">Each chart shows one reading per clear satellite view.</p>
        {vm.indices.map((index) => (
          <IndexDetailCard key={index.key} index={index} />
        ))}

        {/* Weather */}
        {(vm.temperature || vm.rainfall) && (
          <>
            <h2 className="mt-2 font-sans text-[21px] font-bold text-[#111827]">
              Weather in the same period
            </h2>
            <p className="mb-3 text-xs text-[#6b7280]">Daily values, {vm.weatherDayCount} days.</p>
            {vm.temperature ? (
              <div className="mb-4 rounded-[14px] border border-[#e4e8e2] bg-white px-5 py-4">
                <h3 className="font-sans text-lg font-bold text-[#111827]">Temperature (°C)</h3>
                <WeatherLineChart points={vm.temperature.points} color="#c9552b" />
                <StatGrid
                  cols={3}
                  items={[
                    {
                      label: 'Average',
                      value: `${formatFieldHealthValue(vm.temperature.stats.mean, 1)} °C`,
                      color: '#c9552b',
                    },
                    { label: 'Hottest day', value: `${formatFieldHealthValue(vm.temperature.stats.max, 1)} °C` },
                    { label: 'Coolest day', value: `${formatFieldHealthValue(vm.temperature.stats.min, 1)} °C` },
                  ]}
                />
              </div>
            ) : null}
            {vm.rainfall ? (
              <div className="mb-4 rounded-[14px] border border-[#e4e8e2] bg-white px-5 py-4">
                <h3 className="font-sans text-lg font-bold text-[#111827]">Rainfall (mm)</h3>
                <WeatherBarChart points={vm.rainfall.points} color="#1d78c9" />
                <StatGrid
                  items={[
                    {
                      label: 'Total',
                      value: `${formatFieldHealthValue(vm.rainfall.total ?? 0, 0)} mm`,
                      color: '#1d78c9',
                    },
                    {
                      label: 'Average / day',
                      value: formatFieldHealthValue((vm.rainfall.total ?? 0) / vm.rainfall.points.length, 1),
                    },
                    {
                      label: 'Wettest day',
                      value: `${formatFieldHealthValue(vm.rainfall.wettest?.value ?? 0, 1)} mm`,
                      sub: vm.rainfall.wettest?.dateLabel,
                    },
                    { label: 'Days over 10 mm', value: String(vm.rainfall.wetDays ?? 0) },
                  ]}
                />
              </div>
            ) : null}
          </>
        )}

        {/* Glossary */}
        <h2 className="font-sans text-[21px] font-bold text-[#111827]">How to read this report</h2>
        <div className="mt-2 rounded-[14px] border border-[#e4e8e2] bg-white px-5 py-4 text-xs leading-relaxed text-[#374151] columns-1 sm:columns-2 sm:gap-8">
          <p className="mb-1 break-inside-avoid">
            <b className="text-[#008540]">Greenness (NDVI, EVI, MSAVI2)</b> — higher means more healthy green cover.
            They differ in how they handle thick crops and bare soil.
          </p>
          <p className="mb-1 break-inside-avoid">
            <b className="text-[#008540]">Dashed circle</b> — a reading that dropped sharply, usually from cloud or haze.
            It is not counted.
          </p>
          <p className="mb-1 break-inside-avoid">
            <b className="text-[#008540]">Trend</b> — compares the first and latest clear readings. It is a guide, not a
            forecast.
          </p>
          <p className="mb-1 break-inside-avoid">
            <b className="text-[#008540]">Weather</b> — daily temperature and rainfall for the field area over the same
            dates.
          </p>
        </div>

        <p className="mt-6 border-t border-[#e4e8e2] pt-3 text-[9px] text-[#6b7280]">
          Digisaka: Spatial Intelligence Platform · This is a field estimate, not a guarantee.
        </p>
      </div>
    </article>
  );
}
